import { URLSearchParams } from 'url';

import Cookies from 'js-cookie';

import { UserDetailsPartial } from 'src/auth/types';
import { StepEnum } from 'src/features/step-list/constants';
import { PathNames } from 'src/shared/enum/PathNames';
import { BookingState } from 'src/shared/types/booking-state';
import { FALLBACK_LNG, LanguagesCode } from 'src/i18n/constants';

import { RoommateData } from '../features/multi-occupancy/types';

import { PaymentPlanSelection } from './types/PaymentPlanSelection';
import { AppStorage } from './types/AppStorage';
import { DualOccupant } from './types/DualOccupant';

const STORAGE_KEY = 'app_student_booking';
const APP_VERSION = '1.0.2';
const AGENT_CODE_COOKIE_NAME = 'VSB_AGENT_CODE';
const AGENT_CODE_COOKIE_DURATION_DAYS = 1;
const BASKET_STORAGE_KEY = 'vsb_basket';

interface BasketStorage {
    id: string;
    updated_date: string;
}

const REDIRECT_STATE_PARAM_KEY_MAP: Record<string, string> = {
    academicYear: 'academicYear',
    roomCode: 'roomCode',
    room: 'roomCode',
    pid: 'pid',
    promotionId: 'pid',
    cbl: 'cbl',
    rebooker: 'rebooker',
    paymentPlanId: 'paymentPlanId',
    agentCode: 'agentCode',
    agentcode: 'agentCode',
    agentStudentId: 'agentStudentId',
    agentstudentid: 'agentStudentId',
};

const getStorage = (): AppStorage => {
    const storage = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!storage || storage.version !== APP_VERSION) {
        return {
            version: APP_VERSION,
            supabase: null,
            navigationState: {
                page: PathNames.SUMMARY,
                step: StepEnum.YourDetails,
            },
            redirectState: {
                roomCode: null,
                academicYear: null,
                pid: null,
                cbl: null,
                rebooker: null,
                paymentPlanId: null,
                agentCode: null,
                agentStudentId: null,
            },
            user: null,
            booking: null,
            paymentPlanSelection: null,
            dualOccupant: null,
            language: FALLBACK_LNG,
            amend: {
                vitaRef: null,
                roommates: [],
                selfServe: false,
            },
        };
    }

    return storage;
};

const setStorage = (data: Partial<AppStorage>) => {
    const storage = getStorage();
    const newStorage = { ...storage, ...data };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newStorage));
};

const removeStorage = () => {
    localStorage.removeItem(STORAGE_KEY);
};

const setNavigationState = (path: PathNames, step: StepEnum) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        navigationState: {
            page: path,
            step,
        },
    });
};

const clearStorageOnRoomOrYearChange = (roomCode: string, academicYear: string) => {
    const storage = getStorage();
    if (
        storage.redirectState.roomCode &&
        storage.redirectState.academicYear &&
        (storage.redirectState.roomCode !== roomCode ||
            storage.redirectState.academicYear !== academicYear)
    ) {
        removeStorage();
    }
};

const getUserStorage = (): UserDetailsPartial | null => {
    const storage = getStorage();
    return storage.user;
};

const getBasketFromStorage = (): BasketStorage | null => {
    const basket = localStorage.getItem(BASKET_STORAGE_KEY);
    if (!basket) return null;

    try {
        const data: BasketStorage = JSON.parse(basket);

        if (!data || typeof data.id !== 'string' || typeof data.updated_date !== 'string') {
            return null;
        }

        return data;
    } catch {
        return null;
    }
};

const setBasketInStorage = (basket: BasketStorage) => {
    localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify(basket));
};

const clearBasketStorage = () => {
    localStorage.removeItem(BASKET_STORAGE_KEY);
};

const setUserStorage = (data: UserDetailsPartial) => {
    const storage = getStorage();
    const newUserDetails = { ...storage.user, ...data };
    setStorage({ user: newUserDetails });
};

const getAcademicYear = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { academicYear = null } = storage.redirectState;
    const academicYearSearchParam = searchParams.get('academicYear');
    const isSearchParams = !searchParams.size;

    const hasAcademicYearSearchParam =
        typeof academicYearSearchParam === 'string' && academicYearSearchParam.trim().length > 0;

    if (hasAcademicYearSearchParam) {
        return academicYearSearchParam;
    }

    if (academicYear && academicYear.trim().length > 0 && isSearchParams) {
        return academicYear;
    }

    return null;
};

const getRoomCode = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { roomCode = null } = storage.redirectState;
    const roomCodeSearchParam = searchParams.get('roomCode');
    const isSearchParams = !searchParams.size;

    const hasRoomCodeSearchParam =
        typeof roomCodeSearchParam === 'string' && roomCodeSearchParam.trim().length > 0;

    if (hasRoomCodeSearchParam) {
        return roomCodeSearchParam;
    }

    if (roomCode && roomCode.trim().length > 0 && isSearchParams) {
        return roomCode;
    }

    return null;
};

const getPromotionId = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { pid = null } = storage.redirectState;
    const pidSearchParam = searchParams.get('pid');
    const isSearchParams = !searchParams.size;

    const hasPidSearchParam =
        typeof pidSearchParam === 'string' && pidSearchParam.trim().length > 0;

    if (hasPidSearchParam) {
        return pidSearchParam;
    }

    if (pid && pid.trim().length > 0 && isSearchParams) {
        return pid;
    }

    return null;
};

const getCbl = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { cbl = null } = storage.redirectState;
    const cblSearchParam = searchParams.get('cbl');
    const isSearchParams = !searchParams.size;

    const hasCblSearchParam =
        typeof cblSearchParam === 'string' && cblSearchParam.trim().length > 0;

    if (hasCblSearchParam) {
        return cblSearchParam;
    }

    if (cbl && cbl.trim().length > 0 && isSearchParams) {
        return cbl;
    }

    return null;
};

const getRebooker = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { rebooker = null } = storage.redirectState;
    const rebookerSearchParam = searchParams.get('rebooker');
    const isSearchParams = !searchParams.size;

    const hasRebookerSearchParam =
        typeof rebookerSearchParam === 'string' && rebookerSearchParam.trim().length > 0;

    if (hasRebookerSearchParam) {
        return rebookerSearchParam;
    }

    if (rebooker && rebooker.trim().length > 0 && isSearchParams) {
        return rebooker;
    }

    return null;
};

const getPaymentPlanId = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { paymentPlanId = null } = storage.redirectState;
    const paymentPlanIdSearchParam = searchParams.get('paymentPlanId');
    const isSearchParams = !searchParams.size;

    const hasPaymentPlanIdSearchParam =
        typeof paymentPlanIdSearchParam === 'string' && paymentPlanIdSearchParam.trim().length > 0;

    if (hasPaymentPlanIdSearchParam) {
        return paymentPlanIdSearchParam;
    }

    if (paymentPlanId && paymentPlanId.trim().length > 0 && isSearchParams) {
        return paymentPlanId;
    }

    return null;
};

const getAgentCode = (searchParams: URLSearchParams): string | null => {
    const storedAgentCode = Cookies.get(AGENT_CODE_COOKIE_NAME) ?? null;
    const storage = getStorage();
    const agentCodeSearchParam = searchParams.get('agentCode') || searchParams.get('agentcode');

    const hasAgentCodeSearchParam =
        typeof agentCodeSearchParam === 'string' && agentCodeSearchParam.trim().length > 0;

    if (hasAgentCodeSearchParam) {
        Cookies.set(AGENT_CODE_COOKIE_NAME, agentCodeSearchParam, {
            expires: AGENT_CODE_COOKIE_DURATION_DAYS,
        });

        setStorage({
            ...storage,
            redirectState: {
                ...storage.redirectState,
                agentCode: agentCodeSearchParam,
            },
        });

        return agentCodeSearchParam;
    }

    if (storedAgentCode && storedAgentCode.trim().length > 0) {
        return storedAgentCode;
    }

    return null;
};

const getAgentStudentId = (searchParams: URLSearchParams): string | null => {
    const storage = getStorage();
    const { agentStudentId: storedAgentStudentId = null } = storage.redirectState;
    const agentStudentIdSearchParam = searchParams.get('agentStudentId');
    const isSearchParams = !searchParams.size;

    const hasAgentStudentIdSearchParam =
        typeof agentStudentIdSearchParam === 'string' &&
        agentStudentIdSearchParam.trim().length > 0;

    if (hasAgentStudentIdSearchParam) {
        return agentStudentIdSearchParam;
    }

    if (storedAgentStudentId && storedAgentStudentId.trim().length > 0 && isSearchParams) {
        return storedAgentStudentId;
    }
    return null;
};

const setBookingStorage = (data: BookingState) => {
    const storage = getStorage();
    const newBooking = { ...storage.booking, ...data };
    setStorage({ booking: newBooking });
};

const getBookingStorage = (): BookingState | null => {
    const storage = getStorage();
    return storage.booking;
};

const setPaymentPlanSelectionStorage = (data: PaymentPlanSelection) => {
    const storage = getStorage();
    const newPaymentPlan = {
        ...storage.paymentPlanSelection,
        ...data,
    };
    setStorage({ paymentPlanSelection: newPaymentPlan });
};

const getPaymentPlanSelectionStorage = (): PaymentPlanSelection | null => {
    const storage = getStorage();
    const { paymentPlanSelection } = storage;

    return paymentPlanSelection;
};

const setDualOccupantData = (data: DualOccupant) => {
    setStorage({ ...getStorage(), dualOccupant: data });
};

const getDualOccupantData = (): DualOccupant | null => {
    const storage = getStorage();
    return new DualOccupant(storage.dualOccupant);
};

const setLanguageStorage = (language: LanguagesCode) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        language,
    });
};

const setVitaRefStorage = (vitaRef: string) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        amend: {
            vitaRef,
            roommates: storage.amend.roommates,
            selfServe: storage.amend.selfServe,
        },
    });
};

const setAmendRoommatesStorage = (data: RoommateData[]) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        amend: {
            vitaRef: storage.amend.vitaRef,
            roommates: data,
            selfServe: storage.amend.selfServe,
        },
    });
};

const setAmendSelfServeStorage = (selfServe: boolean) => {
    const storage = getStorage();
    setStorage({
        ...storage,
        amend: {
            vitaRef: storage.amend.vitaRef,
            roommates: storage.amend.roommates,
            selfServe,
        },
    });
};

const getAmendSelfServeStorage = (): boolean => {
    const storage = getStorage();
    return storage.amend.selfServe;
};

const setRedirectStateFromParams = (params: URLSearchParams) => {
    const storage = getStorage();
    const redirectStateParams = Array.from(params.entries()).reduce<Record<string, string>>(
        (accumulator, [key, value]) => {
            const redirectStateKey = REDIRECT_STATE_PARAM_KEY_MAP[key];
            if (redirectStateKey) {
                accumulator[redirectStateKey] = value;
            }
            return accumulator;
        },
        {}
    );

    setStorage({
        ...storage,
        redirectState: {
            ...storage.redirectState,
            ...redirectStateParams,
        },
    });
};

const getRedirectState = () => {
    const storage = getStorage();
    const { redirectState } = storage;
    return redirectState;
};

const getAmend = () => {
    const storage = getStorage();
    const { amend } = storage;
    return amend;
};

export {
    removeStorage,
    getAcademicYear,
    getRoomCode,
    getPromotionId,
    getCbl,
    getRebooker,
    getPaymentPlanId,
    getAgentCode,
    getAgentStudentId,
    getStorage,
    getUserStorage,
    getBasketFromStorage,
    setBasketInStorage,
    clearBasketStorage,
    setNavigationState,
    clearStorageOnRoomOrYearChange,
    setStorage,
    setUserStorage,
    setBookingStorage,
    getBookingStorage,
    setPaymentPlanSelectionStorage,
    getPaymentPlanSelectionStorage,
    setDualOccupantData,
    getDualOccupantData,
    setLanguageStorage,
    setVitaRefStorage,
    setAmendRoommatesStorage,
    setAmendSelfServeStorage,
    setRedirectStateFromParams,
    getRedirectState,
    getAmend,
    getAmendSelfServeStorage,
};
